// src/components/Modals/AddHouseModal.jsx
import React, { useState, useEffect } from 'react';
import './ModalBase.css';
import { FaTimes } from 'react-icons/fa';
import { Map, Marker, NavigationControl, FullscreenControl } from 'react-map-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import Select from 'react-select';
import API_BASE_URL from '../../config';

/* ──────────── константы ──────────── */
const CENTER = { latitude: 69.3498, longitude: 88.2031 }; // Норильск
const MAPBOX_TOKEN =
  'pk.eyJ1IjoiaWdvcnNlcmdlZXZpY2hpc2l0IiwiYSI6ImNtNm5oMXd0aTA2bnQycXNnODlycjJtcGEifQ.NSbRJio0u0pnKfQkBluU_Q'; // ← замените своим токеном

/* ──────────── модалка ──────────── */
export default function AddHouseModal({ onClose, onAdded }) {
  /* поля формы */
  const [address, setAddress] = useState('');
  const [floors, setFloors] = useState('');
  const [apartments, setApartments] = useState('');
  const [seriesOptions, setSeriesOptions] = useState([]);
  const [selectedSeries, setSelectedSeries] = useState(null);
  const [year, setYear] = useState('');

  /* координаты маркера */
  const [marker, setMarker] = useState(CENTER);

  /* состояние карты */
 const [viewState, setViewState] = useState(() => {
   const saved = localStorage.getItem('addHouseViewport');
   return saved
     ? JSON.parse(saved)
     : {
         longitude: 88.193,
         latitude: 69.3515,
         zoom: 13.4,
         pitch: 0,
         bearing: 57
       };
 });

  /* загрузка списка серий */
  useEffect(() => {
    fetch(`${API_BASE_URL}/building/series_all`)
      .then((r) => r.json())
      .then((data) =>
        setSeriesOptions(data.map((s) => ({ value: s.id, label: s.name })))
      )
      .catch(console.error);
  }, []);

  /* клик по карте → переставляем маркер */
  const handleMapClick = (e) => {
    const { lng, lat } = e.lngLat; // у v7 lngLat — объект
    setMarker({ latitude: lat, longitude: lng });
  };

  /* отправка формы */
  const handleSave = () => {
    if (!address.trim() || !selectedSeries) {
      alert('Заполните адрес и выберите серию');
      return;
    }

    const q = new URLSearchParams();
    q.append('address', address.trim());
    if (floors) q.append('floors', floors);
    if (apartments) q.append('count_appartaments', apartments);
    q.append('series_id', selectedSeries.value);
    q.append('latitude', marker.latitude);
    q.append('longitude', marker.longitude);
    if (year) q.append('construction_year', year);

    fetch(`${API_BASE_URL}/building/add_build?${q.toString()}`, { method: 'POST' })
      .then((r) => {
        if (!r.ok) throw new Error(r.status);
        return r.json();
      })
      .then((created) => {
        /* 1️⃣ формируем оптимистичный объект */
        const now = Date.now();
        const newHouse = {
          id:          created.id ?? created,          // что вернёт API
          address,
          floors,
          count_appartaments: apartments,
          series_id:   selectedSeries.value,
          series_name: selectedSeries.label,
          construction_year: year,
          latitude:  marker.latitude,
          longitude: marker.longitude,
          timestamp: now
        };

        const STORAGE_ADDED_KEY = 'addedHouses';
        const ONE_HOUR = 60 * 60 * 1000;
        const stored = JSON.parse(localStorage.getItem(STORAGE_ADDED_KEY) || '[]')
                          .filter(h => now - h.timestamp < ONE_HOUR);
        stored.push(newHouse);
        localStorage.setItem(STORAGE_ADDED_KEY, JSON.stringify(stored));

        if (onAdded) onAdded(newHouse.id);
        onClose();
      })
      .catch((e) => {
        console.error(e);
        alert('Не удалось добавить дом, проверьте данные.');
      });
  };

  return (
    <div className="modal-overlay">
      <div className="modal-window add-house-modal pop">
        <button className="modal-close-btn" onClick={onClose}>
          <FaTimes />
        </button>

        <h2 className="modal-title">Добавить новый дом</h2>

        <div className="modal-form">
          {/* карта */}
          <label>Положение на карте:</label>
          <div className="modal-map-container">
            <Map
            {...viewState}
            onMove={(evt) => {
              setViewState(evt.viewState);                       // обновляем стейт
              localStorage.setItem(                              // и кэшируем
                'addHouseViewport',
                JSON.stringify(evt.viewState)
              );
            }}
              style={{ width: '100%', height: '100%' }}
              mapStyle="mapbox://styles/mapbox/dark-v11"
              mapboxAccessToken={MAPBOX_TOKEN}
              onClick={handleMapClick}
              dragRotate                                // вращение мышью
              touchRotate                               // вращение жестами
            >
              <FullscreenControl position="top-left" />
              <NavigationControl position="top-left" />
              <Marker
                latitude={marker.latitude}
                longitude={marker.longitude}
                anchor="bottom"
              >
                <div className="house-marker" />
              </Marker>
            </Map>
          </div>
          <label>Адрес:</label>
          <input
            className="modal-input"
            value={address}
            onChange={(e) => setAddress(e.target.value)}
            placeholder="Адрес"
          />
          <label>Серия дома:</label>
          <Select
            className="modal-select"
            classNamePrefix="modal-select"
            options={seriesOptions}
            value={selectedSeries}
            onChange={setSelectedSeries}
            placeholder="Выберите серию"
          />

          <label>Количество этажей:</label>
          <input
            className="modal-input"
            value={floors}
            onChange={(e) => setFloors(e.target.value.replace(/\D+/g, ''))}
            placeholder="Этажей"
          />
          <label>Количество квартир:</label>
          <input
            className="modal-input"
            value={apartments}
            onChange={(e) => setApartments(e.target.value.replace(/\D+/g, ''))}
            placeholder="Квартир"
          />
          {/* текстовые поля */}

          <label>Год постройки:</label>
          <input
            className="modal-input"
            value={year}
            onChange={(e) => setYear(e.target.value.replace(/\D+/g, ''))}
            placeholder="Год"
          />
        </div>

        <div className="modal-actions">
          <button className="modal-button cancel" onClick={onClose}>
            Отмена
          </button>
          <button className="modal-button save" onClick={handleSave}>
            Сохранить
          </button>
        </div>
      </div>
    </div>
  );
}
